import axios from 'axios';
import semver from 'semver';
import {
  groupsMap,
} from '../../shared/constants';

// The import throws error with decrypt toString conversion so using require for now (and it works as expected)
// eslint-disable-next-line @typescript-eslint/no-var-requires
const CryptoJS = require('crypto-js');

export enum AUTH {
  PRIVILEGES = 'privileges',
  TOKEN = 'token',
  USERID = 'userID',
  USERDBID = 'userDBId',
  JWTTOKEN = 'jwtToken',
}

interface LogiGroupIdsAndRolesArgs {
  externalDashboardIP: string
  logiAdminAccount: string
  logiAdminPswd: string
  logiAdminUserName: string
  privileges: string[]
}

interface LogiTokenAuthArgs {
  user: string;
  externalDashboardIP: string;
  logiOAuthClientId: string;
  logiOAuthClientName: string;
  logiOAuthClientSecret: string;
  logiSupervisorUserName: string;
  logiSupervisorPswd: string;
}

export const decryptText = (encryptedText: string): string => {
  const byteText = CryptoJS.AES.decrypt(encryptedText, 'This is Awesome Dashboard');
  return byteText.toString(CryptoJS.enc.Utf8);
};

export const setAuth = (name: AUTH, value: any, stringify?: boolean) => {
  localStorage.setItem(name, stringify ? JSON.stringify(value) : value);
};

export const getAuth = (name: AUTH) => {
  return localStorage.getItem(name);
};

export const removeAuth = (name: AUTH) => {
  localStorage.removeItem(name);
};

export const clearAuth = () => {
  localStorage.clear();
};

export const getLogiGroupIdsAndRoles = async ({
  externalDashboardIP,
  logiAdminAccount,
  logiAdminPswd,
  logiAdminUserName,
  privileges,
}: LogiGroupIdsAndRolesArgs) => {
  // const roles = basicRolesSet;
  const groups = [] as string[];
  const groupIds = [] as string[];

  // Always add regular user group by default
  groups.push(groupsMap.dashboard_user);

  privileges.forEach((privilege: string) => {
    if (groupsMap[privilege]) {
      groups.push(groupsMap[privilege]);
    }
  });

  try {
    const logiGroupsUrl = `${externalDashboardIP}/composer/api/groups?limit=-1&offset=0`;

    const { data: groupObjs } = await axios.get(logiGroupsUrl, {
      headers: {
        Accept: 'application/vnd.composer.v3+json',
      },
      auth: {
        username: decryptText(logiAdminUserName),
        password: decryptText(logiAdminPswd),
      },
    });

    groupObjs.content.forEach((groupObj: any) => {
      if (groups.includes(groupObj.label)) {
        groupIds.push(groupObj.id);
      }
    });
  } catch (err) {
    // TODO: Log the error
    // console.log('Error while getting Logi group IDs and roles: ', err);
  }

  return {
    groupIds,
    // roles,
  };
};

export const getLogiVersion = async (externalDashboardIP: string) => {
  const versionUrl = `${externalDashboardIP}/composer/api/version`;
  const { data: { version } } = await axios.get(versionUrl);
  console.log(`Detected Logi version: ${version}`);
  return version;
}

export const generateLogiToken = async ({
  user,
  externalDashboardIP,
  logiOAuthClientId,
  logiOAuthClientName,
  logiOAuthClientSecret,
  logiSupervisorUserName,
  logiSupervisorPswd,
}: LogiTokenAuthArgs) => {
  const version = await getLogiVersion(externalDashboardIP);
  const commonArgs = {
    user,
    externalDashboardIP,
    logiOAuthClientId,
    logiOAuthClientName,
    logiSupervisorUserName,
    logiSupervisorPswd,
  };
  const token = semver.gte(version, '23.0.0')
  ? await generateLogiTokenForV23({ ...commonArgs, logiOAuthClientSecret })
  : await generateLogiTokenForV6({ ...commonArgs, logiOAuthClientSecret: '' });

  const { tokenValue, expiration } = token;
  localStorage.setItem('logi_auth_token', tokenValue);
  localStorage.setItem('logi_auth_token_expiration', expiration);

  console.log(`logi_auth_token ${tokenValue}`);
  console.log(`logi_auth_token_expiration ${expiration}`);
  return token;
}

export const generateLogiTokenForV6 = async ({
    user,
    externalDashboardIP,
    logiOAuthClientId,
    logiOAuthClientName,
    logiOAuthClientSecret,
    logiSupervisorUserName,
    logiSupervisorPswd,
  }: LogiTokenAuthArgs) => {
  const logiTokenUrl = `${externalDashboardIP}/composer/api/oauth/token?username=${user}`;

  const {
    data: { tokenValue, expiration },
  } = await axios.post(
    logiTokenUrl,
    {},
    {
      headers: {
        Accept: 'application/vnd.composer.v2+json',
        'Content-Type': 'application/vnd.composer.v2+json',
      },
      auth: {
        username: decryptText(logiSupervisorUserName),
        password: decryptText(logiSupervisorPswd),
      },
      data: {
        clientId: logiOAuthClientId,
        clientName: logiOAuthClientName,
      },
    },
  );

  console.log(`logi_auth_token ${tokenValue}`);
  console.log(`logi_auth_token_expiration ${expiration}`);

  return {
    tokenValue,
    expiration,
  };
}

export const generateLogiTokenForV23 = async ({
  user,
  externalDashboardIP,
  logiOAuthClientId,
  logiOAuthClientName,
  logiOAuthClientSecret,
  logiSupervisorUserName,
  logiSupervisorPswd,
}: LogiTokenAuthArgs) => {
  const logiTokenUrl = `${externalDashboardIP}/composer/api/trusted-access/push/tokens`;

  console.log(`generateLogiTokenForV23, logiOAuthClientId: ${logiOAuthClientId}, logiOAuthClientSecret:${logiOAuthClientSecret}`);

  const {
    data: { access_token: tokenValue, expires_in: expiration },
  } = await axios.post(
    logiTokenUrl,
    { username: user, account: 'Visual Data Discovery' },
    {
      headers: {
        Accept: 'application/vnd.composer.v3+json',
        'Content-Type': 'application/vnd.composer.v3+json',
      },
      auth: {
        username: logiOAuthClientId,
        password: logiOAuthClientSecret,
      },
      data: {
        clientId: logiOAuthClientId,
        clientName: logiOAuthClientName,
        groups: ['Regular Users']
      },
    },
  );

  return {
    tokenValue,
    expiration,
  };
};
